/**
 * VIP 质检 - 用户信息查询 业务工具集
 *
 * 职责：
 *  - 查询条件校验
 *  - 复制文本组装
 *  - 剪贴板写入（兜底 execCommand）
 *  - 高亮段解析：服务端用 {{xxx}} 包裹聊天关键词与「上学/凌晨」标签，
 *    parseHighlightSegments 把文本拆成 text / highlight 片段，
 *    由 HighlightText 组件按 variant 渲染成 <mark> 或 <el-tag>
 */

export function getDeviceLoginRecords(row) {
  if (!row) return [];
  return Array.isArray(row.deviceLoginRecordsTop5) ? row.deviceLoginRecordsTop5 : [];
}

export function getAbnormalDeviceLoginRecords(row) {
  if (!row) return [];
  return Array.isArray(row.abnormalDeviceLoginRecordsTop5) ? row.abnormalDeviceLoginRecordsTop5 : [];
}

export function getPlatformText(row) {
  if (!row) return '-';
  return row.platformStr || '-';
}

/**
 * 角色 ID 简单格式校验
 *  - 允许数字/字母/下划线/连字符，3-64 位
 */
function isValidRoleId(roleId) {
  return /^[0-9A-Za-z_-]{3,64}$/.test(roleId);
}

/**
 * 查询条件校验
 *  - 用户名 / 角色 ID / 角色名 / 手机号 至少填一项
 *  - 角色 ID 简单格式
 */
export function validateQuery(query) {
  const userName = (query.user_name || '').trim();
  const roleId = (query.role_id || '').trim();
  const roleName = (query.role_name || '').trim();
  const mobile = (query.mobile || '').trim();

  if (!userName && !roleId && !roleName && !mobile) {
    return {
      ok: false,
      msg: '请至少填写「角色 ID」或「用户名」或「角色名」或「手机号」任意一项后再搜索',
    };
  }
  if (roleId && !isValidRoleId(roleId)) {
    return {
      ok: false,
      msg: '角色 ID 格式不合法（仅允许 3-64 位 数字/字母/下划线/连字符）',
    };
  }
  return { ok: true };
}

/**
 * 解析 {{xxx}} 包裹的高亮段
 * 输入："妈妈我不想去{{上学}}了，能{{退款}}吗"
 * 输出：[
 *   { type: 'text', value: '妈妈我不想去' },
 *   { type: 'highlight', value: '上学' },
 *   { type: 'text', value: '了，能' },
 *   { type: 'highlight', value: '退款' },
 *   { type: 'text', value: '吗' },
 * ]
 * 使用 v-for + 组件 props 渲染，避免 v-html，天然 XSS safe
 */
export function parseHighlightSegments(text) {
  if (text == null) return [];
  const str = String(text);
  const segments = [];
  const re = /\{\{([\s\S]+?)\}\}/g;
  let lastIndex = 0;
  let match;
  while ((match = re.exec(str)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ type: 'text', value: str.slice(lastIndex, match.index) });
    }
    segments.push({ type: 'highlight', value: match[1] });
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < str.length) {
    segments.push({ type: 'text', value: str.slice(lastIndex) });
  }
  return segments;
}

export function buildCopyText(row) {
  const copyList = row && Array.isArray(row.copyList) ? row.copyList : [];
  return copyList.map(item => `${item.field}：${item.value}`).join('\n');
}

/**
 * 复制到剪贴板
 */
export function copyToClipboard(text) {
  if (!text) return Promise.reject(new Error('empty'));
  if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText) {
    return navigator.clipboard.writeText(text);
  }
  return new Promise((resolve, reject) => {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      ta.style.top = '0';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      ok ? resolve() : reject(new Error('execCommand failed'));
    } catch (e) {
      reject(e);
    }
  });
}
